import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  SignOut, Buildings, ArrowClockwise, ArrowLeft, MagnifyingGlass, Globe,
  Package, ClockCounterClockwise, CheckCircle, TrendUp,
} from "@phosphor-icons/react";
import { api, setToken, clearToken, getToken, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";

const STATUS_CLS = {
  nuevo: "text-[#ff4500] border-[#ff4500]/40 bg-[#ff4500]/10",
  contactado: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10",
  aprobado: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
};
const NEXT = { nuevo: "contactado", contactado: "aprobado", aprobado: "nuevo" };

function LangButton() {
  const { t, toggle } = useI18n();
  return (
    <button onClick={toggle} data-testid="lang-toggle"
      className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[#a3a3a3] hover:text-[#ff4500] transition-colors" aria-label="language">
      <Globe size={16} /> {t.lang.switch}
    </button>
  );
}

function LoginView({ onLogin }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      onLogin(data.user);
      toast.success(t.admin.loggedIn);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.admin.loginError);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-[#0a0a0a] border border-white/10 rounded-none px-4 py-3.5 text-sm text-[#f5f5f5] placeholder:text-[#555] font-plex focus:outline-none focus:border-[#ff4500] focus:ring-1 focus:ring-[#ff4500] transition-colors";

  return (
    <div className="min-h-screen bg-[#050505] grid-lines flex items-center justify-center px-4">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-[#111] border border-white/10 p-8 md:p-10"
      >
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#a3a3a3] hover:text-[#ff4500]">
            <ArrowLeft size={14} /> {t.admin.back}
          </Link>
          <LangButton />
        </div>
        <img src="/esoesly-tile.png" alt="ESOESLY" className="w-14 h-14 rounded-lg mb-6" />
        <h1 className="font-clash font-black uppercase text-3xl text-[#f5f5f5] tracking-tight">{t.admin.panel}</h1>
        <p className="font-plex text-sm text-[#a3a3a3] mt-2 mb-8">{t.admin.panelSub}</p>
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block font-mono text-[11px] uppercase tracking-wider text-[#a3a3a3] mb-2">{t.admin.email}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@wholesale.com" className={input} data-testid="login-email" />
          </div>
          <div>
            <label className="block font-mono text-[11px] uppercase tracking-wider text-[#a3a3a3] mb-2">{t.admin.password}</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className={input} data-testid="login-password" />
          </div>
          <button type="submit" disabled={loading}
            data-testid="login-submit"
            className="w-full bg-[#ff4500] hover:bg-[#e03c00] disabled:opacity-60 text-[#050505] font-semibold py-3.5 transition-colors">
            {loading ? t.admin.verifying : t.admin.login}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#111] border border-white/10 p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <Icon size={22} className="text-[#ff4500]" weight="bold" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#555]">{label}</span>
      </div>
      <p className="font-clash font-black text-4xl text-[#f5f5f5]">{value}</p>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const { t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, nuevo: 0, contactado: 0, aprobado: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      toast.error(t.admin.loadError);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const cycle = async (lead) => {
    const next = NEXT[lead.status] || "nuevo";
    try {
      await api.patch(`/leads/${lead.id}`, { status: next });
      toast.success(`${t.admin.statusTo} ${t.admin.statusLabels[next]}`);
      load();
    } catch {
      toast.error(t.admin.updateError);
    }
  };

  const filtered = leads.filter(
    (l) => l.company_name.toLowerCase().includes(q.toLowerCase()) || l.email.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <div className="noise-overlay" />
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/esoesly-tile.png" alt="ESOESLY" className="w-8 h-8 rounded-md" />
            <span className="font-clash font-black text-lg text-[#f5f5f5]">ESO<span className="text-[#ff4500]">ESLY</span></span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#a3a3a3] border-l border-white/10 pl-3">{t.admin.panelTop}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-xs text-[#a3a3a3]">{user.email}</span>
            <LangButton />
            <button onClick={load} data-testid="refresh-btn" className="text-[#a3a3a3] hover:text-[#ff4500] transition-colors" aria-label="refresh">
              <ArrowClockwise size={18} />
            </button>
            <button onClick={onLogout} data-testid="logout-btn"
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider border border-white/15 hover:border-[#ff4500] text-[#f5f5f5] px-4 py-2 transition-colors">
              <SignOut size={15} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-clash font-black uppercase text-4xl md:text-5xl text-[#f5f5f5] tracking-tight">{t.admin.title}</h1>
          <p className="font-plex text-[#a3a3a3] mt-2">{t.admin.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Package} testid="stat-total" label={t.admin.stats.total} value={stats.total} />
          <StatCard icon={TrendUp} testid="stat-nuevo" label={t.admin.stats.nuevo} value={stats.nuevo} />
          <StatCard icon={ClockCounterClockwise} testid="stat-contactado" label={t.admin.stats.contactado} value={stats.contactado} />
          <StatCard icon={CheckCircle} testid="stat-aprobado" label={t.admin.stats.aprobado} value={stats.aprobado} />
        </div>

        <div className="flex items-center gap-3 mb-4 max-w-md">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.admin.search}
              data-testid="search-input"
              className="w-full bg-[#111] border border-white/10 rounded-none pl-9 pr-4 py-2.5 text-sm text-[#f5f5f5] placeholder:text-[#555] font-plex focus:outline-none focus:border-[#ff4500]" />
          </div>
        </div>

        <div className="bg-[#111] border border-white/10 overflow-x-auto">
          <table className="w-full text-left" data-testid="leads-table">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-[#555] border-b border-white/10">
                <th className="px-5 py-4">{t.admin.cols.company}</th>
                <th className="px-5 py-4">{t.admin.cols.tax}</th>
                <th className="px-5 py-4 hidden md:table-cell">{t.admin.cols.contact}</th>
                <th className="px-5 py-4 hidden lg:table-cell">{t.admin.cols.volume}</th>
                <th className="px-5 py-4">{t.admin.cols.status}</th>
                <th className="px-5 py-4 hidden sm:table-cell">{t.admin.cols.date}</th>
              </tr>
            </thead>
            <tbody className="font-plex text-sm">
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-[#a3a3a3]">{t.admin.loading}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-[#a3a3a3]" data-testid="empty-state">
                  <Buildings size={32} className="mx-auto mb-3 text-[#333]" />
                  {t.admin.empty}
                </td></tr>
              ) : (
                filtered.map((l) => {
                  const cls = STATUS_CLS[l.status] || STATUS_CLS.nuevo;
                  return (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-4 font-medium text-[#f5f5f5]">{l.company_name}</td>
                      <td className="px-5 py-4 text-[#a3a3a3] font-mono text-xs">{l.tax_id}</td>
                      <td className="px-5 py-4 hidden md:table-cell text-[#a3a3a3]">
                        <div>{l.email}</div>
                        <div className="text-xs text-[#555]">{l.phone}</div>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell text-[#a3a3a3] text-xs">{l.volume}</td>
                      <td className="px-5 py-4">
                        <button onClick={() => cycle(l)} data-testid={`status-toggle-${l.id}`}
                          className={`font-mono text-[10px] uppercase tracking-wider border px-2.5 py-1 hover:opacity-80 transition-opacity ${cls}`}>
                          {t.admin.statusLabels[l.status] || t.admin.statusLabels.nuevo}
                        </button>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell text-[#555] font-mono text-xs">
                        {l.created_at?.slice(0, 10)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const tok = getToken();
    if (!tok) { setChecking(false); return; }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const logout = () => { clearToken(); setUser(null); toast.success(t.admin.loggedOut); };

  if (checking) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center font-mono text-[#a3a3a3]">{t.admin.loading}</div>;
  }
  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onLogin={setUser} />;
}
